import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from './supabase';
import ImageUploadField from './components/ImageUploadField';
import PDFUploadField from './components/PDFUploadField';
import OnlineClassForm from './components/OnlineClassForm';
import OnlineClassCard from './components/OnlineClassCard';
import { OnlineClass } from './types';
import { getGoogleDriveDirectLink } from './utils/uploadUtils';

interface PDFFile {
  url: string;
  name: string;
  type?: string;
  description?: string;
}

export default function DashboardPage() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [hasProfile, setHasProfile] = useState<boolean | null>(null);
  const [profileCheckDone, setProfileCheckDone] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [docId, setDocId] = useState<string | null>(null);
  const navigate = useNavigate();

  // Profile form states
  const [fullName, setFullName] = useState('');
  const [mobile, setMobile] = useState('');
  const [subject, setSubject] = useState('');
  const [district, setDistrict] = useState('');
  const [specificLocation, setSpecificLocation] = useState('');
  const [medium, setMedium] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [bio, setBio] = useState('');
  const [education, setEducation] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [pdfUrls, setPdfUrls] = useState<PDFFile[]>([]);
  const [draftPdfUrls, setDraftPdfUrls] = useState<PDFFile[]>([]);
  const [savingMaterials, setSavingMaterials] = useState(false);
  const saveTimerRef = useRef<number | null>(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [onlineClasses, setOnlineClasses] = useState<OnlineClass[]>([]);
  const [loadingClasses, setLoadingClasses] = useState(false);

  const districts = [
    'Colombo', 'Gampaha', 'Kalutara', 'Kandy', 'Matale', 'Nuwara Eliya', 'Galle', 'Matara', 'Hambantota',
    'Jaffna', 'Kilinochchi', 'Mannar', 'Vavuniya', 'Mullaitivu', 'Batticaloa', 'Ampara', 'Trincomalee',
    'Kurunegala', 'Puttalam', 'Anuradhapura', 'Polonnaruwa', 'Badulla', 'Monaragala', 'Ratnapura', 'Kegalle'
  ];

  useEffect(() => {
    const initDashboard = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { navigate('/login'); return; }

      const currentUser = session.user;
      setUser(currentUser);

      // Pre-fill from Google account metadata if available
      if (currentUser.user_metadata?.full_name && !fullName) {
        setFullName(currentUser.user_metadata.full_name);
      }
      if (currentUser.user_metadata?.avatar_url && !imageUrl) {
        setImageUrl(currentUser.user_metadata.avatar_url);
      }

      await checkProfile(currentUser.id);
      setLoading(false);
    };
    initDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    setDraftPdfUrls(pdfUrls);
  }, [pdfUrls]);
  // In this specific structure, we mainly want to run this once on mount/auth change.
  // We can ignore the warning or wrap checkProfile in useCallback.
  // For now, let's keep it simple and just rely on auth state change.

  const checkProfile = async (userId: string) => {
    try {
      const { data: teacherData } = await supabase.from('teachers').select('*').eq('user_id', userId).maybeSingle();

      if (!teacherData) {
        setHasProfile(false);
        setIsEditing(true);
      } else {
        const teacherId = teacherData.id;
        setHasProfile(true);
        setDocId(teacherId);

        setFullName(teacherData.name || '');
        setMobile(teacherData.mobile || '');
        setSubject(teacherData.subject || '');
        setDistrict(teacherData.district || '');
        setSpecificLocation(teacherData.specific_location || '');
        setMedium(teacherData.medium || '');
        setWhatsapp(teacherData.whatsapp || '');
        setBio(teacherData.bio || '');
        setEducation(teacherData.education || '');
        setImageUrl(teacherData.image_url || '');
        setPdfUrls(teacherData.pdf_urls || []);

        await loadOnlineClasses(teacherId);
      }
    } catch {
      setHasProfile(false);
    } finally {
      setProfileCheckDone(true);
    }
  };

  const loadOnlineClasses = async (teacherId: string) => {
    try {
      setLoadingClasses(true);
      const { data } = await supabase.from('online_classes').select('*').eq('teacher_id', teacherId);
      const classes: OnlineClass[] = (data || []).map((row: any) => ({
        id: row.id,
        teacherId: row.teacher_id,
        title: row.title,
        subject: row.subject,
        teacherName: row.teacher_name,
        date: row.schedule ? row.schedule.split('T')[0] : '',
        time: row.schedule ? row.schedule.split('T')[1]?.slice(0, 5) : '',
        classLink: row.meeting_link,
        description: row.description,
        maxStudents: row.max_students,
        registeredStudents: row.registered_students || 0,
      }));
      setOnlineClasses(classes);
    } catch { /* silently fail */ } finally {
      setLoadingClasses(false);
    }
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccessMsg('');

    try {
      const profileData = {
        user_id: user.id,
        name: fullName,
        email: user.email,
        mobile,
        whatsapp,
        subject,
        district,
        specific_location: specificLocation,
        medium,
        bio: bio || `${subject} teacher in ${district}${specificLocation ? ', ' + specificLocation : ''}. Teaching in ${medium} medium.`,
        education,
        image_url: imageUrl,
        pdf_urls: pdfUrls,
      };

      if (hasProfile && docId) {
        const { error } = await supabase.from('teachers').update(profileData).eq('id', docId);
        if (error) throw error;
        setSuccessMsg('Profile updated successfully!');
        setIsEditing(false);
      } else {
        const { data, error } = await supabase.from('teachers').insert(profileData).select('id').single();
        if (error) throw error;
        setDocId(data.id);
        setHasProfile(true);
        setSuccessMsg('Profile created! You can now be found on the Find Teachers page.');
        setIsEditing(false);
      }

    } catch (error: any) {
      setError('Failed to save profile: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleMaterialsAutoSave = async (nextPdfs: PDFFile[]) => {
    if (!docId) return;
    setSavingMaterials(true);
    setError('');
    try {
      const { error } = await supabase.from('teachers').update({ pdf_urls: nextPdfs }).eq('id', docId);
      if (error) throw error;
      setPdfUrls(nextPdfs);
    } catch (err: any) {
      setError('Failed to save materials: ' + err.message);
    } finally {
      setSavingMaterials(false);
    }
  };

  const handleMaterialsChange = (nextPdfs: PDFFile[]) => {
    setDraftPdfUrls(nextPdfs);
    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = window.setTimeout(() => {
      void handleMaterialsAutoSave(nextPdfs);
    }, 600);
  };

  const toggleEdit = () => {
    setIsEditing(!isEditing);
  };

  if (loading || !profileCheckDone) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 48, height: 48, border: '3px solid #e2e8f0', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 1rem' }} />
          <p style={{ color: '#64748b' }}>Loading dashboard...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #f8fbff 0%, #f8fafc 36%, #ffffff 100%)', padding: 'clamp(1rem, 4vw, 3rem)' }}>
      <div style={{ maxWidth: '960px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem',
        }}>
          <div style={{ width: '100%', minWidth: 0 }}>
            <span style={{
              display: 'inline-block', background: 'rgba(99,102,241,0.1)', color: '#6366f1',
              padding: '0.35rem 0.9rem', borderRadius: '50px', fontSize: '0.75rem',
              fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.75rem',
            }}>Teacher Dashboard</span>
            <h1 style={{ fontSize: 'clamp(1.25rem, 5vw, 2rem)', fontWeight: 800, color: '#0f172a', margin: 0, letterSpacing: '-0.02em', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
              Welcome, {fullName || user.user_metadata?.full_name || user.user_metadata?.name || user.email}
            </h1>
            <p style={{ margin: '0.65rem 0 0', color: '#64748b', fontSize: '0.95rem', lineHeight: 1.7, maxWidth: '42rem' }}>
              Keep your public profile polished, update study materials, and manage classes from a cleaner mobile dashboard.
            </p>
          </div>
          {hasProfile && !isEditing && (
            <div style={{
              display: 'flex',
              gap: '0.75rem',
              width: '100%',
              flexWrap: 'wrap',
            }}>
              <Link to={`/teacher/${docId}`} className="btn secondary" target="_blank" style={{ flex: '1 1 auto', minWidth: 0 }}>
                View Public Profile
              </Link>
              <button onClick={toggleEdit} className="btn primary" style={{ flex: '1 1 auto', minWidth: 0 }}>
                Edit Profile
              </button>
            </div>
          )}
        </div>

        {isEditing ? (
          <div style={{
            maxWidth: '800px', margin: '0 auto', background: 'rgba(255,255,255,0.96)',
            borderRadius: '24px', padding: 'clamp(1.25rem, 5vw, 2.5rem)', border: '1px solid rgba(148,163,184,0.16)',
            boxShadow: '0 20px 45px rgba(15,23,42,0.06)',
          }}>
            <h2 style={{ marginBottom: '1.5rem', textAlign: 'center', color: '#0f172a', fontWeight: 800 }}>
              {hasProfile ? 'Edit Profile' : 'Complete Your Profile'}
            </h2>

            <form className="auth-form" onSubmit={handleProfileSubmit}>
              {error && <div className="auth-feedback error">{error}</div>}
              {successMsg && <div className="auth-feedback success">{successMsg}</div>}

              <div className="auth-section-card">
                <div className="auth-section-header">
                  <h3 className="auth-section-title">Profile image</h3>
                  <p className="auth-section-copy">Keep your dashboard and public profile looking more trusted with a clear photo.</p>
                </div>
                <ImageUploadField
                  userId={user.id}
                  currentImageUrl={imageUrl}
                  onImageUploaded={setImageUrl}
                  onError={setError}
                />
              </div>

              <div className="auth-section-card">
                <div className="auth-section-header">
                  <h3 className="auth-section-title">Core details</h3>
                  <p className="auth-section-copy">Update the main information students see first.</p>
                </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))', gap: '1rem' }}>
                <input
                  type="text"
                  id="dashboard-full-name"
                  name="fullName"
                  className="input-field"
                  placeholder="Full Name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                />

                <input
                  type="tel"
                  id="dashboard-mobile"
                  name="mobile"
                  className="input-field"
                  placeholder="Mobile Number"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  required
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))', gap: '1rem' }}>
                <input
                  type="text"
                  id="dashboard-subject"
                  name="subject"
                  className="input-field"
                  placeholder="Subject (e.g. Mathematics)"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                />

                <div className="select-wrapper">
                  <select
                    id="dashboard-district"
                    name="district"
                    className="input-field"
                    value={district}
                    onChange={(e) => setDistrict(e.target.value)}
                    required
                    style={{ width: '100%', appearance: 'none', cursor: 'pointer' }}
                  >
                    <option value="" disabled>Select District</option>
                    {districts.map(d => (
                      <option key={d} value={d} style={{ color: '#000' }}>{d}</option>
                    ))}
                  </select>
                </div>
              </div>
              </div>

              <div className="auth-section-card">
                <div className="auth-section-header">
                  <h3 className="auth-section-title">Teaching setup</h3>
                  <p className="auth-section-copy">Clarify location, medium, and the best way students can reach you.</p>
                </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))', gap: '1rem' }}>
                <input
                  type="text"
                  id="dashboard-specific-location"
                  name="specificLocation"
                  className="input-field"
                  placeholder="Specific Location"
                  value={specificLocation}
                  onChange={(e) => setSpecificLocation(e.target.value)}
                />

                <div className="select-wrapper">
                  <select
                    id="dashboard-medium"
                    name="medium"
                    className="input-field"
                    value={medium}
                    onChange={(e) => setMedium(e.target.value)}
                    required
                    style={{ width: '100%', appearance: 'none', cursor: 'pointer' }}
                  >
                    <option value="" disabled>Select Medium</option>
                    <option value="English" style={{ color: '#000' }}>English</option>
                    <option value="Tamil" style={{ color: '#000' }}>Tamil</option>
                    <option value="Sinhala" style={{ color: '#000' }}>Sinhala</option>
                  </select>
                </div>
              </div>

              <input
                type="tel"
                id="dashboard-whatsapp"
                name="whatsapp"
                className="input-field"
                placeholder="WhatsApp Number (e.g. 0771234567)"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                required
              />
              </div>

              <div className="auth-section-card">
                <div className="auth-section-header">
                  <h3 className="auth-section-title">About your teaching</h3>
                  <p className="auth-section-copy">Give students a concise, professional summary of how you teach.</p>
                </div>
              <textarea
                id="dashboard-bio"
                name="bio"
                className="input-field"
                placeholder="Bio (Tell students about yourself)"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                style={{ minHeight: '100px', resize: 'vertical', fontFamily: 'inherit' }}
              />

              <input
                type="text"
                id="dashboard-education"
                name="education"
                className="input-field"
                placeholder="Education / Qualifications (e.g. BSc, BEd, MSc)"
                value={education}
                onChange={(e) => setEducation(e.target.value)}
              />
              </div>

              <div className="auth-section-card">
                <div className="auth-section-header">
                  <h3 className="auth-section-title">Study materials</h3>
                  <p className="auth-section-copy">Upload PDFs that make your profile feel more complete and credible.</p>
                </div>
                <PDFUploadField
                  userId={user.id}
                  currentPDFs={pdfUrls}
                  onPDFsUpdated={setPdfUrls}
                  onError={setError}
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem', flexWrap: 'wrap' }}>
                {hasProfile && (
                  <button type="button" onClick={() => setIsEditing(false)} className="btn tertiary" style={{ flex: '1 1 100%' }}>
                    Cancel
                  </button>
                )}
                <button type="submit" className="btn primary" disabled={saving} style={{ flex: '1 1 100%' }}>
                  {saving ? 'Saving...' : 'Save Profile'}
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div className="profile-summary">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 'clamp(1rem, 4vw, 2rem)',
                background: 'rgba(255,255,255,0.95)', padding: 'clamp(1.25rem, 5vw, 2.5rem)', borderRadius: '24px',
                border: '1px solid rgba(148,163,184,0.16)', boxShadow: '0 20px 45px rgba(15,23,42,0.06)',
                flexWrap: 'wrap', justifyContent: 'center'
              }}>
                <div style={{
                  width: 'clamp(100px, 20vw, 130px)',
                  height: 'clamp(100px, 20vw, 130px)',
                  borderRadius: '20px',
                  overflow: 'hidden',
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  boxShadow: '0 8px 32px rgba(99,102,241,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  {imageUrl ? (
                    <img
                      src={getGoogleDriveDirectLink(imageUrl)}
                      alt={fullName}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        console.error('Dashboard Image failed to load:', imageUrl);
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <span style={{ fontSize: '3rem', color: '#fff', fontWeight: 800 }}>{fullName.charAt(0)}</span>
                  )}
                </div>

                <div style={{ flex: 1, minWidth: 'min(280px, 100%)', textAlign: 'center' }}>
                  <h2 style={{ marginBottom: '0.5rem', color: '#0f172a', fontWeight: 800, fontSize: 'clamp(1.25rem, 4vw, 1.75rem)' }}>{fullName}</h2>
                  <div style={{
                    display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '1rem',
                    justifyContent: 'center'
                  }}>
                    <span style={{ background: 'rgba(99,102,241,0.1)', color: '#6366f1', padding: '0.3rem 0.75rem', borderRadius: '50px', fontSize: '0.78rem', fontWeight: 700 }}>{subject}</span>
                    <span style={{ background: 'rgba(16,185,129,0.1)', color: '#059669', padding: '0.3rem 0.75rem', borderRadius: '50px', fontSize: '0.78rem', fontWeight: 600 }}>{district}</span>
                  </div>
                  <p style={{ color: '#64748b', lineHeight: 1.7, marginBottom: '1.5rem', fontSize: '0.95rem' }}>
                    {bio}
                  </p>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem' }}>
                    {[{ l: 'Mobile', v: mobile }, { l: 'WhatsApp', v: whatsapp }, { l: 'Location', v: specificLocation || '-' }, { l: 'Medium', v: medium }, { l: 'Education', v: education || '-' }].map(item => (
                      <div key={item.l} style={{ background: '#f8fafc', padding: '0.75rem 1rem', borderRadius: '12px', border: '1px solid #f1f5f9' }}>
                        <span style={{ display: 'block', fontSize: '0.65rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{item.l}</span>
                        <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.85rem', marginTop: '0.15rem', wordBreak: 'break-all' }}>{item.v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Study Materials */}
              <div style={{
                background: 'rgba(255,255,255,0.95)', padding: 'clamp(1.25rem, 5vw, 2.5rem)', borderRadius: '24px',
                border: '1px solid rgba(148,163,184,0.16)', boxShadow: '0 20px 45px rgba(15,23,42,0.06)',
              }}>
                <h3 style={{ marginTop: 0, color: '#0f172a', fontWeight: 700, fontSize: '1.1rem' }}>My Study Materials</h3>
                <p style={{ color: '#64748b', marginTop: '0.25rem', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                  Add new PDFs here. Changes auto-save in a moment.
                </p>
                <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '1rem' }}>
                  {savingMaterials ? 'Saving materials...' : (error ? 'Save failed. Please try again.' : 'All changes saved')}
                </div>

                <PDFUploadField
                  userId={user.id}
                  currentPDFs={draftPdfUrls}
                  onPDFsUpdated={handleMaterialsChange}
                  onError={setError}
                />

                {error && (
                  <div style={{ color: '#ef4444', marginTop: '0.75rem', fontSize: '0.875rem' }}>
                    {error}
                  </div>
                )}

                {draftPdfUrls.length > 0 ? (
                  <div style={{ display: 'grid', gap: '0.75rem', marginTop: '1rem' }}>
                    {draftPdfUrls.map((pdf, idx) => (
                      <div key={idx} style={{
                        padding: '1rem', background: '#f8fafc', borderRadius: '14px',
                        border: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        flexWrap: 'wrap', gap: '0.5rem'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: '200px' }}>
                          <div style={{
                            width: 36, height: 36, borderRadius: '8px',
                            background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#fff', fontSize: '0.65rem', fontWeight: 800,
                          }}>PDF</div>
                          <div style={{ display: 'grid', gap: '0.25rem' }}>
                            <a href={pdf.url} target="_blank" rel="noopener noreferrer" style={{ color: '#0f172a', textDecoration: 'none', fontWeight: 600, fontSize: '0.9rem', wordBreak: 'break-all' }}>
                              {pdf.name}
                            </a>
                            {pdf.type && (
                              <span style={{ fontSize: '0.75rem', color: '#6366f1', fontWeight: 600 }}>
                                {pdf.type}
                              </span>
                            )}
                            {pdf.description && (
                              <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                                {pdf.description}
                              </span>
                            )}
                          </div>
                        </div>
                        <a href={pdf.url} target="_blank" rel="noopener noreferrer" style={{ color: '#6366f1', fontSize: '0.85rem', fontWeight: 600, textDecoration: 'none' }}>
                          Download ↓
                        </a>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '2rem', background: '#fafbfc', borderRadius: '14px', border: '1px dashed #e2e8f0', marginTop: '1rem' }}>
                    <p style={{ color: '#64748b', margin: 0, fontSize: '0.9rem' }}>
                      No materials uploaded yet. Use the uploader above to add lecture notes.
                    </p>
                  </div>
                )}
              </div>

              {/* Online Classes Section */}
              <div style={{
                background: 'rgba(255,255,255,0.95)', padding: 'clamp(1.25rem, 5vw, 2.5rem)', borderRadius: '24px',
                border: '1px solid rgba(148,163,184,0.16)', boxShadow: '0 20px 45px rgba(15,23,42,0.06)', marginTop: '2rem'
              }}>
                <h3 style={{ marginTop: 0, color: '#0f172a', fontWeight: 700, fontSize: '1.1rem', marginBottom: '0.5rem' }}>My Online Classes</h3>
                <p style={{ color: '#64748b', marginTop: 0, marginBottom: '1.5rem', fontSize: '0.9rem' }}>
                  Create and manage your online teaching sessions
                </p>

                {hasProfile && !isEditing && (
                  <div style={{ marginBottom: '2rem' }}>
                    <OnlineClassForm
                      teacherId={docId!}
                      teacherName={fullName}
                      subject={subject}
                      onClassCreated={() => loadOnlineClasses(docId!)}
                    />
                  </div>
                )}

                {loadingClasses ? (
                  <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <p style={{ color: '#64748b' }}>Loading classes...</p>
                  </div>
                ) : onlineClasses.length > 0 ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))', gap: '1.5rem' }}>
                    {onlineClasses.map((classItem) => (
                      <OnlineClassCard key={classItem.id} classData={classItem} />
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '2rem', background: '#fafbfc', borderRadius: '14px', border: '1px dashed #e2e8f0' }}>
                    <p style={{ color: '#64748b', margin: 0, fontSize: '0.9rem' }}>
                      No online classes created yet. Click the button above to create your first class!
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
